import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useWalletStore } from '../store/walletStore'
import { Menu, X, User, Wallet, LogOut, Settings, Trophy, Home } from 'lucide-react'
import { useState, useEffect } from 'react'

export default function Navbar() {
  const { user, userData, signOut, hasRole } = useAuthStore()
  const { balance, fetchBalance } = useWalletStore()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    if (user) {
      fetchBalance()
    }
  }, [user, fetchBalance])

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  return (
    <nav className="bg-kiniela-bg shadow-md sticky top-0 z-50 border-b border-kiniela-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <img 
                src="/kiniela_logo.png" 
                alt="Kiniela.mx Logo" 
                className="h-10 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-kiniela-gold">Kiniela.mx</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/dashboard"
              className="flex items-center space-x-1 text-kiniela-text hover:text-kiniela-gold transition"
            >
              <Home size={18} />
              <span>Inicio</span>
            </Link>
            <Link
              to="/quinielas"
              className="flex items-center space-x-1 text-kiniela-text hover:text-kiniela-gold transition"
            >
              <Trophy size={18} />
              <span>Quinielas</span>
            </Link>

            {/* Wallet Balance */}
            {balance && (
              <Link
                to="/wallet"
                className="flex items-center space-x-2 bg-kiniela-accent/20 px-4 py-2 rounded-lg hover:bg-kiniela-accent/30 border border-kiniela-border transition"
              >
                <Wallet size={18} className="text-kiniela-muted" />
                <div className="text-sm">
                  <div className="font-semibold text-kiniela-gold">
                    ${balance.total?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </Link>
            )}

            {/* Admin/Operator Links */}
            {hasRole('admin') && (
              <Link
                to="/admin"
                className="text-kiniela-text hover:text-kiniela-gold transition font-medium"
              >
                Admin
              </Link>
            )}
            {hasRole('operator') && !hasRole('admin') && (
              <Link
                to="/operator"
                className="text-kiniela-text hover:text-kiniela-gold transition font-medium"
              >
                Operador
              </Link>
            )}

            {/* User Menu */}
            <div className="relative group">
              <button className="flex items-center space-x-2 text-kiniela-text hover:text-kiniela-gold transition">
                <User size={20} />
                <span className="text-sm font-medium">{userData?.username || 'Usuario'}</span>
              </button>

              {/* Dropdown */}
              <div className="absolute right-0 mt-2 w-48 bg-kiniela-card border border-kiniela-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 px-4 py-3 hover:bg-kiniela-bg transition text-kiniela-text hover:text-kiniela-gold"
                >
                  <Settings size={18} />
                  <span>Mi Perfil</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center space-x-2 px-4 py-3 hover:bg-kiniela-bg transition text-kiniela-cta"
                >
                  <LogOut size={18} />
                  <span>Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-kiniela-text hover:text-kiniela-gold"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-kiniela-border bg-kiniela-card">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/dashboard"
              className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Inicio
            </Link>
            <Link
              to="/quinielas"
              className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Quinielas
            </Link>
            <Link
              to="/wallet"
              className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Billetera
            </Link>
            <Link
              to="/profile"
              className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Mi Perfil
            </Link>
            <Link
              to="/terminos"
              className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Términos y Condiciones
            </Link>
            {hasRole('admin') && (
              <Link
                to="/admin"
                className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </Link>
            )}
            {hasRole('operator') && !hasRole('admin') && (
              <Link
                to="/operator"
                className="block px-3 py-2 rounded-md text-kiniela-text hover:bg-kiniela-bg hover:text-kiniela-gold"
                onClick={() => setMobileMenuOpen(false)}
              >
                Operador
              </Link>
            )}
            <button
              onClick={() => {
                handleSignOut()
                setMobileMenuOpen(false)
              }}
              className="w-full text-left block px-3 py-2 rounded-md text-kiniela-cta hover:bg-kiniela-bg"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      )}
    </nav>
  )
}
